import { DEAUTHENTICATE } from "../actions/constants";

export type LoadingState = { [key: string]: boolean };
export type LoadedAtState = { [key: string]: number };
export type ErrorsState = { [key: string]: any };
export type RequestStatusesState = {
  loading: LoadingState;
  loadedAt: LoadedAtState;
  errors: ErrorsState;
};

export const initialState: RequestStatusesState = {
  loading: {},
  loadedAt: {},
  errors: {},
};

const reducer = (
  state: RequestStatusesState = initialState,
  action: {
    type: string;
    timestamp: number;
    requestKey?: string;
    requestSuccess?: boolean;
    requestFailure?: boolean;
    requestSkip?: boolean;
    requestResetErrors?: boolean;
    payload?: { response?: any; error?: any; body?: any };
  }
): RequestStatusesState => {
  const { requestKey, timestamp, payload, type } = action;
  if (type === DEAUTHENTICATE) return initialState;
  if (!requestKey) return state;

  const success = action.requestSuccess || payload?.response !== undefined;
  const failure =
    action.requestFailure || payload?.error || payload instanceof Error;
  const resetErrors = action.requestResetErrors;
  const skip = action.requestSkip;

  if (!(success || failure || skip)) {
    // REQUEST
    let { errors } = state;
    if (errors[requestKey]) {
      errors = { ...errors };
      delete errors[requestKey];
    }
    return {
      ...state,
      loading: {
        ...state.loading,
        [requestKey]: true,
      },
      errors,
    };
  } else if (success) {
    // SUCCESS
    return {
      ...state,
      loading: {
        ...state.loading,
        [requestKey]: false,
      },
      loadedAt: {
        ...state.loadedAt,
        [requestKey]: timestamp,
      },
    };
  } else if (failure) {
    // FAILURE
    return {
      ...state,
      loading: {
        ...state.loading,
        [requestKey]: false,
      },
      errors: {
        ...state.errors,
        [requestKey]: payload?.error || payload?.body || payload,
      },
    };
  } else if (resetErrors) {
    // RESET ERRORS
    if (typeof action.payload === "string") {
      // reset a single error
      return {
        ...state,
        errors: {
          ...state.errors,
          [requestKey]: {
            ...[state.errors[requestKey]],
            [action.payload]: undefined,
          },
        },
      };
    } else if (Array.isArray(action.payload)) {
      // reset multiple errors
      return {
        ...state,
        errors: {
          ...state.errors,
          [requestKey]: {
            ...[state.errors[requestKey]],
            ...action.payload.reduce((o, k) => {
              o[k] = undefined;
              return o;
            }, {}),
          },
        },
      };
    } else if (typeof action.payload === "object") {
      // replace errors with payload
      return {
        ...state,
        errors: {
          ...state.errors,
          [requestKey]: action.payload,
        },
      };
    }
    // reset all errors
    return {
      ...state,
      errors: {
        ...state.errors,
        [requestKey]: undefined,
      },
    };
  } else if (skip) {
    // SKIP
    return {
      ...state,
      loading: {
        ...state.loading,
        [requestKey]: false,
      },
    };
  }

  return state;
};

export default reducer;

export const selectRequestStatuses = ({
  requestStatuses,
}: {
  requestStatuses: RequestStatusesState;
}) => requestStatuses;

export const selectLoading = (
  {
    requestStatuses,
  }: {
    requestStatuses: RequestStatusesState;
  },
  requestKey: string
) => requestStatuses.loading[requestKey];

export const selectIsLoading = (
  {
    requestStatuses,
  }: {
    requestStatuses: RequestStatusesState;
  },
  requestKey: string
) => !!requestStatuses.loading[requestKey];

export const selectIsLoadingAny = (
  {
    requestStatuses,
  }: {
    requestStatuses: RequestStatusesState;
  },
  requestKeyRegExp: RegExp
) =>
  Object.keys(requestStatuses.loading).some(
    (k) => requestStatuses.loading[k] && k.match(requestKeyRegExp)
  );

export const selectHasBeenRequested = (
  {
    requestStatuses,
  }: {
    requestStatuses: RequestStatusesState;
  },
  requestKey: string
) => Object.hasOwnProperty.call(requestStatuses.loading, requestKey);

export const selectHasBeenRequestedAny = (
  {
    requestStatuses,
  }: {
    requestStatuses: RequestStatusesState;
  },
  requestKeyRegExp: RegExp
) =>
  Object.keys(requestStatuses.loading).some((k) => k.match(requestKeyRegExp));

export const selectLoadedAt = (
  {
    requestStatuses,
  }: {
    requestStatuses: RequestStatusesState;
  },
  requestKey: string
) => requestStatuses.loadedAt[requestKey];

export const selectHasLoaded = (
  {
    requestStatuses,
  }: {
    requestStatuses: RequestStatusesState;
  },
  requestKey: string
) => !!requestStatuses.loadedAt[requestKey];

export const selectHasLoadedAny = (
  {
    requestStatuses,
  }: {
    requestStatuses: RequestStatusesState;
  },
  requestKeyRegExp: RegExp
) =>
  Object.keys(requestStatuses.loadedAt).some(
    (k) => requestStatuses.loadedAt[k] && k.match(requestKeyRegExp)
  );
